// Shared bits for the Billing module.
import React, { useLayoutEffect, useRef } from 'react';

/**
 * Textarea that auto-grows with its content.
 *
 * Uses `useLayoutEffect` so the height re-measures on every value change —
 * including the async edit-mode hydration where form items arrive after the
 * initial render. Also opts into the modern `field-sizing: content` CSS so
 * browsers that support it (Chrome 123+, Firefox 122+, Safari 17.4+) get
 * native instant growth without any JS measurement at all.
 */
export const AutoGrowTextarea = React.forwardRef(function AutoGrowTextarea(
  { value, minRows = 3, maxHeight = 480, className = '', style, ...rest },
  forwardedRef,
) {
  const innerRef = useRef(null);
  const setRefs = (el) => {
    innerRef.current = el;
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value, maxHeight]);
  return (
    <textarea
      {...rest}
      ref={setRefs}
      value={value ?? ''}
      rows={minRows}
      className={className}
      // `field-sizing: content` — modern native auto-grow. Falls back gracefully.
      style={{ fieldSizing: 'content', ...style }}
    />
  );
});


export const STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  converted: 'Converted',
  cancelled: 'Cancelled',
  expired: 'Expired',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
};

export const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-600 border-slate-300',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  converted: 'bg-violet-50 text-violet-700 border-violet-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
  expired: 'bg-orange-50 text-orange-700 border-orange-200',
  partially_paid: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  overdue: 'bg-red-50 text-red-700 border-red-200',
};

export const EST_STATUSES = ['draft', 'sent', 'accepted', 'converted', 'cancelled', 'expired'];
export const INV_STATUSES = ['draft', 'sent', 'partially_paid', 'paid', 'overdue', 'cancelled'];

// Indian formatter — 1,51,550.00 instead of 151,550.00
export const fmtINR = (n) => {
  const num = Number(n || 0);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const whole = Math.floor(abs);
  const frac = Math.round((abs - whole) * 100);
  const s = String(whole);
  let head;
  if (s.length <= 3) head = s;
  else {
    const last3 = s.slice(-3);
    let rest = s.slice(0, -3);
    const groups = [];
    while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
    if (rest) groups.unshift(rest);
    head = groups.join(',') + ',' + last3;
  }
  return `${sign}${head}.${String(frac).padStart(2, '0')}`;
};

export const todayISO = () => new Date().toISOString().slice(0, 10);

// A locked (unpaid) workshop gets a 402 on every write. TenantContext already
// shows one global toast with a Reactivate action, so component-level error UI
// must stay quiet rather than stack a second message on top of it.
export const isSubscriptionErr = (e) =>
  e?.response?.status === 402 && e?.response?.data?.code === 'subscription_required';

export const formatErr = (e) => {
  if (isSubscriptionErr(e)) return '';
  const detail = e?.response?.data?.detail;
  // A 5xx often never reaches us as JSON — the edge/CDN substitutes its own
  // HTML page, and showing that to a workshop owner is worse than useless.
  if (!detail && e?.response?.status >= 500) {
    return 'The server could not complete that just now. Please try again in a moment.';
  }
  if (!detail) return e?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      const field = [...(d.loc || [])].reverse().find((x) => typeof x === 'string' && x !== 'body');
      const label = field ? field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '';
      const msg = /valid (integer|number)/i.test(d.msg || '') ? 'please enter a number or leave it blank' : (d.msg || JSON.stringify(d));
      return label ? `${label}: ${msg}` : msg;
    }).join(' ');
  }
  return JSON.stringify(detail);
};

export const inputCls =
  'w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 sm:py-2 ' +
  'text-base sm:text-sm text-slate-900 ' +
  'placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition';

export const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// GST-estimate per-line math. Mirrors backend `_gst_line_math` in
// billing_pdf.py — including the tax-inclusive round-figure back-calculation
// used when `line_total_target > 0`. Keeps rate + total in lockstep as the
// user types.
export const computeGstLine = (it) => {
  const qty = Number(it.qty) || 0;
  const rate = Number(it.rate) || 0;
  const g = Number(it.gst_rate) || 0;
  const d = Number(it.discount_pct) || 0;
  const target = r2(Number(it.line_total_target) || 0);
  if (target > 0) {
    const tax = g ? r2((target * g) / (100 + g)) : 0;
    const taxable = r2(target - tax);
    const gross = d > 0 && d < 100 ? r2(taxable / (1 - d / 100)) : taxable;
    return { gross, taxable, tax, total: target };
  }
  const gross = r2(qty * rate);
  const dAmt = r2((gross * d) / 100);
  const taxable = r2(gross - dAmt);
  const tax = r2((taxable * g) / 100);
  return { gross, taxable, tax, total: r2(taxable + tax) };
};

export const REVENUE_HEADS = ['Detailing', 'Mechanical Servicing', 'Body Shop', 'Accessories', 'Consumables and Others'];

const HEAD_KEYWORDS = [
  ['Detailing', ['ceramic', 'coating', 'ppf', 'paint protection', 'sun film', 'sunfilm', 'tint', 'interior detail', 'polish', 'wax', 'buff']],
  ['Body Shop', ['repaint', 'paint job', 'denting', 'dent removal', 'panel beat', 'panel-beat', 'body work', 'insurance repair']],
  ['Mechanical Servicing', ['service', 'servicing', 'repair', 'brake', 'clutch', 'battery', 'oil change', 'coolant', 'suspension', 'engine', 'parts replacement', 'spare']],
  ['Accessories', ['accessory', 'accessories', 'mat', 'seat cover', 'mudflap', 'spoiler', 'roof carrier', 'chrome', 'led']],
];

export const guessHead = (text) => {
  const t = (text || '').toLowerCase();
  for (const [head, kws] of HEAD_KEYWORDS) {
    if (kws.some((k) => t.includes(k))) return head;
  }
  return '';
};

export const UNITS = ['NOS', 'SET', 'JOB', 'KGS', 'LTR', 'SQF', 'MTR'];

export const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[status] || STATUS_COLOR.draft}`}
    data-testid={`status-${status}`}
  >
    {STATUS_LABEL[status] || status}
  </span>
);

// Use instead of `alert(formatErr(e))` so a locked account doesn't get a blank
// dialog on top of the subscription toast.
export const notifyErr = (e) => {
  const msg = formatErr(e);
  if (msg) alert(msg);
};
